import { useState } from 'react';
import { DateRangePicker, type ArmedField } from './DateRangePicker';
import { daysBetween } from '../../lib/dates';

// "Thu" + "7 Aug" faces for the date card (local-day safe).
function dvParts(iso: string): { dow: string; dm: string } {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
  return {
    dow: dt.toLocaleDateString('en-GB', { weekday: 'short' }),
    dm: dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
  };
}
// Span between the two dates: exclusive nights (bookings) or inclusive days (leave).
function span(from: string, to: string, unit: 'night' | 'day'): number {
  const n = daysBetween(from, to);
  return unit === 'day' ? n + 1 : n;
}

export interface DateCardFieldProps {
  from: string; // '' when unset
  to: string; // '' when unset
  today: string; // ISO
  unit?: 'night' | 'day';
  labels?: { from: string; to: string }; // defaults by unit
  onChange: (from: string, to: string) => void;
}

// The date editor used by the booking and leave panes: the three-cell .datecard
// (from · count · to) that opens the shared DateRangePicker in a .calmodal. Source
// of truth = the booking pane's date editor, promoted to a global so both panes
// share one picker and one overlay.
export function DateCardField({ from, to, today, unit = 'night', labels, onChange }: DateCardFieldProps) {
  const [calOpen, setCalOpen] = useState(false);
  // Armed-field picker (staff-food-at-house): the cell you click is the field
  // you're editing; both dates must exist for armed mode (a fresh empty range
  // keeps the classic two-tap flow).
  const [armed, setArmed] = useState<ArmedField>('in');
  const open = (which: ArmedField) => {
    setArmed(which);
    setCalOpen((o) => (o && armed === which ? false : true));
  };
  const lab = labels ?? (unit === 'day' ? { from: 'From', to: 'To' } : { from: 'Check-in', to: 'Check-out' });
  const hasBoth = !!from && !!to;
  const count = hasBoth ? span(from, to, unit) : null;
  const noun = unit === 'day' ? 'day' : 'night';

  const cell = (iso: string) => iso
    ? (() => { const p = dvParts(iso); return <><span className="dow">{p.dow}</span> {p.dm}</>; })()
    : <span className="dph">Select…</span>;

  return (
    <>
      <div className="datecard">
        <button type="button" className={'dcell' + (calOpen && armed === 'in' ? ' active' : '')} onClick={() => open('in')}>
          <div className="dk">{lab.from}</div><div className="dv">{cell(from)}</div>
        </button>
        {/* the nights counter is a readout, not a control (Freddi) */}
        <div className="dmid">
          <div className="dn">{count == null ? '—' : count}</div><div className="du">{count === 1 ? noun : `${noun}s`}</div>
        </div>
        <button type="button" className={'dcell r' + (calOpen && armed === 'out' ? ' active' : '')} onClick={() => open('out')}>
          <div className="dk">{lab.to}</div><div className="dv">{cell(to)}</div>
        </button>
      </div>
      {calOpen && (
        <div className="calmodal" onMouseDown={() => setCalOpen(false)}>
          <div className="calcard" onMouseDown={(e) => e.stopPropagation()}>
            <DateRangePicker checkIn={from || null} checkOut={to || null} today={today} unit={unit}
              armed={hasBoth ? armed : undefined} onArmedChange={setArmed} fieldLabels={lab}
              onChange={(r) => onChange(r.checkIn ?? '', r.checkOut ?? '')} onDone={() => setCalOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}
